import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Collapse,
  Skeleton,
  Tooltip,
  Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import BalanceIcon from "@mui/icons-material/Balance";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CheckIcon from "@mui/icons-material/Check";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getAllPortfolios,
  getPortfolioById,
  updatePortfolio,
  removeHoldingFromPortfolio,
  deletePortfolio,
} from "../../../api/portfolio_api";
import CreatePortfolio from "./createPortfolio";
import AddHoldingsDialog from "./addHoldingsDialog";
import EditSharesDialog from "./editSharesCountDialog";
import RebalanceDialog from "./RebalanceDialog";
import BulkEditDialog from "./BulkEditDialog";
import { Cell, Pie, PieChart } from "recharts";
import { Edit2Icon } from "lucide-react";
import { fmtNumber, fmtPercent } from "./portfolioHelpers";
import { getCurrencySymbol } from "../../../utils/currency";

// const fmtNumber = (v, d = 2) =>
//   v === null || v === undefined || isNaN(v)
//     ? "—"
//     : Number(v).toLocaleString(undefined, {
//       minimumFractionDigits: d,
//       maximumFractionDigits: d,
//     });

// const fmtPercent = (v, d = 2) =>
//   v === null || v === undefined || isNaN(v)
//     ? "—"
//     : `${v >= 0 ? "+" : ""}${fmtNumber(v, d)}%`;

/** Color helper: returns green for positive, red for negative, muted for null/zero */
const gainColor = (v) => {
  if (v === null || v === undefined) return "var(--color-text-secondary)";
  const n = Number(v);
  if (isNaN(n)) return "var(--color-text-secondary)";
  if (n > 0) return "var(--color-success)";
  if (n < 0) return "var(--color-error)";
  return "var(--color-text-secondary)";
};

const deriveTotals = (holdings = []) => {
  let value = 0;
  let gain = 0;

  holdings.forEach((h) => {
    value += Number(h.current_value || 0);
    gain += Number(h.gain_loss || 0);
  });

  return {
    value,
    gain,
    pct: value ? (gain / (value - gain)) * 100 : 0,
  };
};

const market = sessionStorage.getItem("selectedMarket") ?? "US";

const HEADER_TOOLTIPS = {
  Portfolio: "Portfolio name — click the arrow to expand holdings",
  Add: "Add or bulk-edit holdings in this portfolio",
  "Total %": "Total gain / loss as a percentage",
  Value: "Current market value of all holdings",
  "Daily Chg": "Change in portfolio value since yesterday's close",
  "Daily %": "Daily change expressed as a percentage",
  "Total chg": "Absolute gain / loss since initial purchase",
};

const HOLDING_TOOLTIPS = {
  Ticker: "Stock ticker symbol — click to open the detail page",
  Company: "Full company name",
  Quantity: "Number of quanitity held",
  "Total Cost": "Total amount invested (cost basis)",
  Price: "Latest market price per quanitity",
  Value: "Current market value of the position",
  "Daily Chg": "Change in position value since yesterday's close",
  "Daily %": "Daily change expressed as a percentage",
  Volume: "Today's trading volume for this stock",
  Weight: "Position weight as a percentage of the portfolio",
  Actions: "Edit quantity or remove this holding",
};

/* ================= SKELETON LOADERS ================= */

const HEADER_COLUMNS = ["Portfolio", "Add", "Total %", "Value", "Daily Chg", "Daily %", "Total chg"];
const COL_COUNT = HEADER_COLUMNS.length;

const PortfolioTableSkeleton = () => (
  <Table
    size="small"
    sx={{
      minWidth: 900, // forces horizontal scroll on small screens
    }}
  >    <TableHead>
      <TableRow sx={{ bgcolor: "var(--color-surface-hover)" }}>
        {HEADER_COLUMNS.map((header, idx) => (
          <TableCell
            key={idx}
            sx={{
              fontSize: "var(--font-size-sm)",
              fontWeight: 700,
              color: "var(--color-text-primary)",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              borderBottom: "1px solid var(--color-border-primary)",
              py: 1.25,
            }}
          >
            <Tooltip title={HEADER_TOOLTIPS[header] ?? ""} placement="top" arrow>
              <span style={{ cursor: "default" }}>{header}</span>
            </Tooltip>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
    <TableBody>
      {[...Array(3)].map((_, idx) => (
        <TableRow
          key={idx}
          sx={{
            bgcolor: "var(--color-surface)",
            borderBottom: "1px solid var(--color-border-primary)",
          }}
        >
          {[120, 60, 60, 80, 80, 60, 80].map((w, ci) => (
            <TableCell key={ci} sx={{ py: 1.25, borderBottom: "none" }}>
              <Skeleton
                variant="rectangular"
                width={w}
                height={16}
                sx={{ borderRadius: 0.5, bgcolor: "var(--color-surface-hover)" }}
              />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

const AssetConcentrationSkeleton = () => (
  <Box p={2}>
    <Box
      sx={{
        width: "100%",
        height: 200,
        bgcolor: "var(--color-bg-secondary)",
        borderRadius: 1.5,
        border: "1px solid var(--color-border-primary)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        mb: 2,
      }}
    >
      <Skeleton
        variant="circular"
        width={160}
        height={160}
        sx={{ bgcolor: "var(--color-surface-hover)" }}
      />
    </Box>

    <Box>
      <Skeleton
        variant="rectangular"
        width={100}
        height={12}
        sx={{ borderRadius: 0.5, bgcolor: "var(--color-surface-hover)", mb: 1.5 }}
      />

      {[...Array(5)].map((_, idx) => (
        <Box
          key={idx}
          sx={{
            py: 1,
            borderBottom: idx < 4 ? "1px solid var(--color-border-primary)" : "none",
          }}
        >
          <Box display="flex" justifyContent="space-between" mb={0.5}>
            <Box>
              <Skeleton
                variant="rectangular"
                width={60}
                height={14}
                sx={{ borderRadius: 0.5, bgcolor: "var(--color-surface-hover)", mb: 0.5 }}
              />
              <Skeleton
                variant="rectangular"
                width={100}
                height={10}
                sx={{ borderRadius: 0.5, bgcolor: "var(--color-surface-hover)" }}
              />
            </Box>
            <Box textAlign="right">
              <Skeleton
                variant="rectangular"
                width={50}
                height={14}
                sx={{ borderRadius: 0.5, bgcolor: "var(--color-surface-hover)", mb: 0.5, ml: "auto" }}
              />
              <Skeleton
                variant="rectangular"
                width={35}
                height={10}
                sx={{ borderRadius: 0.5, bgcolor: "var(--color-surface-hover)", ml: "auto" }}
              />
            </Box>
          </Box>
          <Skeleton
            variant="rectangular"
            width="100%"
            height={3}
            sx={{ borderRadius: 0.5, bgcolor: "var(--color-surface-hover)" }}
          />
        </Box>
      ))}
    </Box>

    <Box mt={2} pt={2} borderTop="1px solid var(--color-border-primary)">
      {[...Array(3)].map((_, idx) => (
        <Box key={idx} display="flex" justifyContent="space-between" mb={1}>
          <Skeleton
            variant="rectangular"
            width={70}
            height={12}
            sx={{ borderRadius: 0.5, bgcolor: "var(--color-surface-hover)" }}
          />
          <Skeleton
            variant="rectangular"
            width={50}
            height={12}
            sx={{ borderRadius: 0.5, bgcolor: "var(--color-surface-hover)" }}
          />
        </Box>
      ))}
    </Box>
  </Box>
);

const PortfolioRow = ({
  row,
  expanded,
  loading,
  onToggle,
  onAdd,
  onBulkEdit,
  onUndoRemove,
  onNameUpdated,
  isSelected,
  onToggleSelection,
}) => {
  const holdings = row.holdings || [];
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(row.name);
  const [saving, setSaving] = useState(false);

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedHolding, setSelectedHolding] = useState(null);

  const [confirmDeleteHolding, setConfirmDeleteHolding] = useState(false);
  const [holdingToDelete, setHoldingToDelete] = useState(null);
  const [deletingHolding, setDeletingHolding] = useState(false);

  const sym = getCurrencySymbol();

  const handleSave = async () => {
    if (!name.trim() || name === row.name) {
      setEditing(false);
      setName(row.name);
      return;
    }

    setSaving(true);
    const res = await updatePortfolio(row.id, { name: name.trim() });
    setSaving(false);

    if (res?.success) onNameUpdated();
    else setName(row.name);

    setEditing(false);
  };

  return (
    <>
      <TableRow
        sx={{
          bgcolor: "var(--color-surface)",
          borderBottom: "1px solid var(--color-border-primary)",
          "&:hover": { bgcolor: "var(--color-surface-hover)" },
        }}
      >
        {/* Column 1: Portfolio name */}
        <TableCell sx={{ py: 1.25, borderBottom: "none" }}>
          <Box display="flex" alignItems="center" gap={0.75}>
            <IconButton
              size="small"
              onClick={() => onToggleSelection(row.id)}
              sx={{
                color: isSelected ? "var(--color-primary)" : "var(--color-text-secondary)",
                "&:hover": { bgcolor: "var(--color-surface-hover)" },
                p: 0.5,
              }}
            >
              {isSelected ? (
                <CheckBoxIcon sx={{ fontSize: 18 }} />
              ) : (
                <CheckBoxOutlineBlankIcon sx={{ fontSize: 18 }} />
              )}
            </IconButton>

            <IconButton
              size="small"
              onClick={() => onToggle(row.id)}
              sx={{
                color: "var(--color-text-secondary)",
                "&:hover": { bgcolor: "var(--color-surface-hover)" },
                p: 0.5,
              }}
            >
              {expanded ? (
                <KeyboardArrowUpIcon sx={{ fontSize: 18 }} />
              ) : (
                <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />
              )}
            </IconButton>

            {editing ? (
              <input
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSave();
                  if (e.key === "Escape") {
                    setEditing(false);
                    setName(row.name);
                  }
                }}
                style={{
                  fontSize: "var(--font-size-base)",
                  fontWeight: 500,
                  border: "1px solid var(--color-border-primary)",
                  borderRadius: 4,
                  padding: "3px 6px",
                  width: 160,
                  outline: "revert",
                  fontFamily: "inherit",
                  backgroundColor: "var(--color-bg-secondary)",
                  color: "var(--color-text-primary)",
                }}
              />
            ) : (
              <Typography variant="paragraph" fontWeight={"var(--font-weight-semibold)"} color='var(--color-text-primary)'>
                {row.name}
              </Typography>
            )}

            {!editing && (
              <IconButton
                size="small"
                onClick={() => setEditing(true)}
                sx={{
                  color: "var(--color-text-secondary)",
                  "&:hover": { bgcolor: "var(--color-surface-hover)", color: "var(--color-text-secondary)" },
                  p: 0.5,
                }}
              >
                <EditOutlinedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}

            {editing && (
              <>
                <IconButton
                  size="small"
                  onClick={handleSave}
                  disabled={saving}
                  sx={{
                    color: "var(--color-success)",
                    "&:hover": { bgcolor: "var(--color-surface-hover)" },
                    p: 0.5,
                  }}
                >
                  {saving ? (
                    <CircularProgress size={14} sx={{ color: "var(--color-success)" }} />
                  ) : (
                    <CheckIcon sx={{ fontSize: 16 }} />
                  )}
                </IconButton>

                <IconButton
                  size="small"
                  onClick={() => {
                    setName(row.name);
                    setEditing(false);
                  }}
                  sx={{
                    color: "var(--color-text-secondary)",
                    "&:hover": { bgcolor: "var(--color-surface-hover)" },
                    p: 0.5,
                  }}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </>
            )}

            <IconButton
              size="small"
              onClick={() => setConfirmDelete(true)}
              sx={{
                color: "var(--color-text-secondary)",
                "&:hover": { bgcolor: "var(--color-surface-hover)", color: "var(--color-error)" },
                p: 0.5,
              }}
            >
              <DeleteOutlineIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </TableCell>

        {/* Column 2: Add / Edit buttons */}
        <TableCell sx={{ py: 1.25, borderBottom: "none" }}>
          {/* <Box display="flex" alignItems="center" gap={2}> */}
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            flexWrap={{ xs: "wrap", md: "nowrap" }}
          >
            <Button
              size="small"
              variant="contained"
              onClick={() => onAdd(row.id)}
              disabled={market === "ALL"}
              sx={{
                fontSize: "var(--font-size-xs2)",
                fontWeight: "var(--font-weight-semibold)",
                textTransform: "none",
                borderRadius: "var(--radius-md)",
                px: 1.5,
                py: 0.5,
                bgcolor: "var(--color-primary)",
                color: "var(--color-text-inverse)",
                boxShadow: "none",
                "&:hover": {
                  bgcolor: "var(--color-primary-dark)",
                  boxShadow: "none",
                },
              }}
            >
              + Add symbols
            </Button>

            <Button
              size="small"
              variant="contained"
              disabled={!holdings.length || market === "ALL"}
              onClick={() => onBulkEdit(row)}
              startIcon={<Edit2Icon size={14} />}
              sx={{
                fontSize: "var(--font-size-xs2)",
                fontWeight: "var(--font-weight-semibold)",
                textTransform: "none",
                borderRadius: "var(--radius-md)",
                px: 1.5,
                py: 0.5,
                bgcolor: holdings.length
                  ? "var(--color-warning)"
                  : "var(--color-surface-active)",
                color: holdings.length
                  ? "var(--color-text-inverse)"
                  : "var(--color-text-secondary)",
                boxShadow: "none",
                "&:hover": {
                  bgcolor: holdings.length
                    ? "#d97706"
                    : "var(--color-surface-active)",
                  boxShadow: "none",
                },
              }}
            >
              Edit
            </Button>
          </Box>
        </TableCell>

        {/* Column 3: Total Gain/Loss % */}
        <TableCell sx={{ py: 1.25, borderBottom: "none" }}>
          <Typography
            variant="paragraph"
            fontWeight="var(--font-weight-semibold)"
            color={gainColor(row.total_gain_loss_pct)}
          >
            {row.total_gain_loss_pct != null ? fmtPercent(row.total_gain_loss_pct) : "—"}
          </Typography>
        </TableCell>

        {/* Column 4: Total Value */}
        <TableCell sx={{ py: 1.25, borderBottom: "none" }}>
          <Typography
            variant="paragraph"
            fontWeight="var(--font-weight-semibold)"
            color="var(--color-text-secondary)"
          >
            {row.total_value != null ? `${sym}${fmtNumber(row.total_value)}` : "—"}
          </Typography>
        </TableCell>

        {/* Column 5: Daily Gain & Loss */}
        <TableCell sx={{ py: 1.25, borderBottom: "none" }}>
          <Typography
            variant="paragraph"
            fontWeight="var(--font-weight-semibold)"
            color={gainColor(row.daily_gain_loss)}
          >
            {row.daily_gain_loss != null ? `${Number(row.daily_gain_loss) >= 0 ? "+" : ""}${sym}${fmtNumber(row.daily_gain_loss)}` : "—"}
          </Typography>
        </TableCell>

        {/* Column 6: Daily % */}
        <TableCell sx={{ py: 1.25, borderBottom: "none" }}>
          <Typography
            variant="paragraph"
            fontWeight="var(--font-weight-semibold)"
            color={gainColor(row.daily_gain_loss_pct)}
          >
            {row.daily_gain_loss_pct != null ? fmtPercent(row.daily_gain_loss_pct) : "—"}
          </Typography>
        </TableCell>

        {/* Column 7: Total Gain & Loss */}
        <TableCell sx={{ py: 1.25, borderBottom: "none" }}>
          <Typography
            variant="paragraph"
            fontWeight="var(--font-weight-semibold)"
            color={gainColor(row.total_gain_loss)}
          >
            {row.total_gain_loss != null ? `${Number(row.total_gain_loss) >= 0 ? "+" : ""}${sym}${fmtNumber(row.total_gain_loss)}` : "—"}
          </Typography>
        </TableCell>
      </TableRow>

      {/* Expanded holdings row */}
      <TableRow>
        <TableCell
          colSpan={COL_COUNT}
          sx={{ p: 0, borderBottom: expanded ? "1px solid var(--color-border-primary)" : "none" }}
        >
          <Collapse in={expanded}>
            <Box bgcolor="var(--color-bg-secondary)" px={1.5} py={1.5}>
              {loading ? (
                [...Array(3)].map((_, i) => (
                  <Skeleton
                    key={i}
                    height={32}
                    sx={{ mb: 0.75, bgcolor: "var(--color-surface)" }}
                  />
                ))
              ) : holdings.length === 0 ? (
                <Box py={3} textAlign="center">
                  <Typography fontSize="var(--font-size-sm)" color="var(--color-text-secondary)" fontWeight={500}>
                    No holdings in this portfolio
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    width: "100%",
                    overflowX: "auto",
                    WebkitOverflowScrolling: "touch",
                  }}
                >
                  <Table
                    size="small"
                    sx={{
                      minWidth: 1100, // expanded table needs more
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        {["Ticker", "Company", "Quantity", "Total Cost", "Price", "Value", "Daily Chg", "Daily %", "Volume", "Weight", "Actions"].map((header, idx) => (
                          <TableCell
                            key={idx}
                            align={header === "Actions" ? "right" : "left"}
                            sx={{
                              fontSize: "var(--font-size-sm)",
                              fontWeight: "var(--font-weight-bold)",
                              color: "var(--color-text-primary)",
                              textTransform: "uppercase",
                              letterSpacing: "0.03em",
                              borderBottom: "1px solid var(--color-border-primary)",
                              py: 1,
                              px: 1,
                            }}
                          >
                            <Tooltip title={HOLDING_TOOLTIPS[header] ?? ""} placement="top" arrow>
                              <span style={{ cursor: "default" }}>{header}</span>
                            </Tooltip>
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {holdings.map((h) => (
                        <TableRow
                          key={h.ticker}
                          sx={{
                            "&:hover": { bgcolor: "var(--color-surface)" },
                            borderBottom: "1px solid var(--color-border-primary)",
                          }}
                        >
                          <TableCell sx={{ py: 1, px: 1, borderBottom: "none" }}>
                            <Typography
                              variant="paragraph"
                              fontWeight="var(--font-weight-semibold)"
                              color="var(--color-primary)"
                              sx={{
                                cursor: "pointer",
                                "&:hover": { textDecoration: "underline" },
                              }}
                              onClick={() =>
                                navigate(`/stocks/${h.ticker}/details`)
                              }
                            >
                              {h.ticker}
                            </Typography>
                          </TableCell>

                          <TableCell sx={{ py: 1, px: 1, borderBottom: "none" }}>
                            <Typography
                              variant="paragraph"
                              fontWeight="var(--font-weight-semibold)"
                              color='var(--color-text-muted)'
                              noWrap>
                              {h.company_name}
                            </Typography>
                          </TableCell>

                          <TableCell sx={{ py: 1, px: 1, borderBottom: "none" }}>
                            <Typography
                              variant="paragraph"
                              fontWeight="var(--font-weight-semibold)"
                              color='var(--color-text-secondary)'>
                              {fmtNumber(h.shares, 2)}
                            </Typography>
                          </TableCell>

                          <TableCell sx={{ py: 1, px: 1, borderBottom: "none" }}>
                            <Typography
                              variant="paragraph"
                              fontWeight="var(--font-weight-semibold)"
                              color="var(--color-text-secondary)">
                              {sym}{fmtNumber(h.cost_basis)}
                            </Typography>
                          </TableCell>

                          <TableCell sx={{ py: 1, px: 1, borderBottom: "none" }}>
                            <Typography
                              variant="paragraph"
                              fontWeight="var(--font-weight-semibold)"
                              color="var(--color-text-secondary)">
                              {sym}{fmtNumber(h.stock_current_price)}
                            </Typography>
                          </TableCell>

                          <TableCell sx={{ py: 1, px: 1, borderBottom: "none" }}>
                            <Typography
                              variant="paragraph"
                              fontWeight="var(--font-weight-semibold)"
                              color='var(--color-text-secondary)'>
                              {sym}{fmtNumber(h.current_value)}
                            </Typography>
                          </TableCell>

                          {/* Daily Change */}
                          <TableCell sx={{ py: 1, px: 1, borderBottom: "none" }}>
                            <Typography
                              variant="paragraph"
                              fontWeight="var(--font-weight-semibold)"
                              color={gainColor(h.daily_change)}
                            >
                              {h.daily_change != null ? `${Number(h.daily_change) >= 0 ? "+" : ""}${sym}${fmtNumber(h.daily_change)}` : "—"}
                            </Typography>
                          </TableCell>

                          {/* Daily Change % */}
                          <TableCell sx={{ py: 1, px: 1, borderBottom: "none" }}>
                            <Typography
                              variant="paragraph"
                              fontWeight="var(--font-weight-semibold)"
                              color={gainColor(h.daily_change_pct)}
                            >
                              {h.daily_change_pct != null ? fmtPercent(h.daily_change_pct) : "—"}
                            </Typography>
                          </TableCell>

                          {/* Volume */}
                          <TableCell sx={{ py: 1, px: 1, borderBottom: "none" }}>
                            <Typography
                              variant="paragraph"
                              fontWeight="var(--font-weight-semibold)"
                              color="var(--color-text-muted)"
                            >
                              {h.daily_volume != null
                                ? Number(h.daily_volume).toLocaleString()
                                : "—"}
                            </Typography>
                          </TableCell>

                          <TableCell sx={{ py: 1, px: 1, borderBottom: "none" }}>
                            <Typography variant="paragraph"
                              fontWeight="var(--font-weight-semibold)" color='var(--color-text-muted)'>
                              {fmtPercent(h.weight_pct)}
                            </Typography>
                          </TableCell>

                          <TableCell
                            align="right"
                            sx={{ py: 1, px: 1, borderBottom: "none" }}
                          >
                            <Box display="flex" gap={0.5} justifyContent="flex-end">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSelectedHolding(h);
                                  setOpenEditDialog(true);
                                }}
                                sx={{
                                  bgcolor: "var(--color-surface-hover)",
                                  color: "var(--color-text-secondary)",
                                  width: 30,
                                  height: 30,
                                  "&:hover": {
                                    bgcolor: "var(--color-surface)",
                                    color: "var(--color-text-primary)",
                                  },
                                }}
                              >
                                <EditOutlinedIcon sx={{ fontSize: 16 }} />
                              </IconButton>

                              <IconButton
                                size="small"
                                onClick={() => {
                                  setHoldingToDelete(h);
                                  setConfirmDeleteHolding(true);
                                }}
                                sx={{
                                  bgcolor: "var(--color-surface)",
                                  color: "var(--color-text-secondary)",
                                  width: 30,
                                  height: 30,
                                  "&:hover": {
                                    bgcolor: "var(--color-error)",
                                    color: "#fff",
                                  },
                                }}
                              >
                                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow >

      {/* Delete Portfolio Dialog */}
      < Dialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "var(--color-surface)",
            backgroundImage: "none",
            borderRadius: 1.5,
            border: "1px solid var(--color-border-primary)",
          },
        }}
      >
        <DialogTitle sx={{ color: "var(--color-text-primary)", borderBottom: "1px solid var(--color-border-primary)", py: 1.5, fontSize: 16, fontWeight: 600 }}>
          Delete portfolio
        </DialogTitle>

        <DialogContent sx={{ pt: 2, pb: 1.5 }}>
          <Typography fontSize="var(--font-size-sm)" color='var(--color-text-muted)' lineHeight={1.6} my={2}>
            Are you sure you want to delete{" "}
            <Typography component="span" fontWeight="var(--font-weight-semibold)" color='var(--color-text-secondary)'>
              {row.name}
            </Typography>
            ? This action cannot be undone.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 2.5, pb: 1.5, borderTop: "1px solid var(--color-border-primary)" }}>
          <Button
            onClick={() => setConfirmDelete(false)}
            disabled={deleting}
            sx={{
              textTransform: "none",
              fontSize: "var(--font-size-sm)",
              fontWeight: 500,
              color: "var(--color-text-secondary)",
              "&:hover": { bgcolor: "var(--color-surface-hover)" },
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            disabled={deleting}
            onClick={async () => {
              setDeleting(true);
              const res = await deletePortfolio(row.id);
              setDeleting(false);
              setConfirmDelete(false);

              if (res?.success) {
                onNameUpdated();
              }
            }}
            startIcon={
              deleting ? <CircularProgress size={14} color="inherit" /> : null
            }
            sx={{
              textTransform: "none",
              fontSize: "var(--font-size-sm)",
              fontWeight: 500,
              bgcolor: "var(--color-error)",
              "&:hover": { bgcolor: "var(--color-error-hover)" },
              boxShadow: "none",
              py: 0.5,
              px: 1.75,
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog >

      {/* Edit Single Holding Dialog */}
      < EditSharesDialog
        open={openEditDialog}
        onClose={() => {
          setOpenEditDialog(false);
          setSelectedHolding(null);
        }}
        portfolioId={row.id}
        holding={selectedHolding}
        onSaved={async (updatedPortfolio) => {
          if (updatedPortfolio) {
            onNameUpdated(updatedPortfolio);
          } else {
            onNameUpdated();
          }
        }}
      />

      {/* Delete Holding Dialog */}
      <Dialog
        open={confirmDeleteHolding}
        onClose={() => setConfirmDeleteHolding(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "var(--color-surface)",
            backgroundImage: "none",
            borderRadius: 1.5,
            border: "1px solid var(--color-border-primary)",
          },
        }}
      >
        <DialogTitle sx={{ color: "var(--color-text-primary)", borderBottom: "1px solid var(--color-border-primary)", py: 1.5, fontSize: 16, fontWeight: 600 }}>
          Remove holding
        </DialogTitle>

        <DialogContent sx={{ pt: 2, pb: 1.5 }}>
          <Typography fontSize="var(--font-size-sm)" color='var(--color-text-muted)' lineHeight={1.6}>
            Are you sure you want to remove{" "}
            <Typography component="span" fontWeight="var(--font-weight-semibold)" color='var(--color-text-secondary)'>
              {holdingToDelete?.ticker}
            </Typography>
            {holdingToDelete?.company_name && (
              <>
                {" "}
                <Typography component="span" color="var(--color-text-secondary)">
                  ({holdingToDelete.company_name})
                </Typography>
              </>
            )}
            {" "}from{" "}
            <Typography component="span" fontWeight="var(--font-weight-semibold)" color='var(--color-text-secondary)'>
              {row.name}
            </Typography>
            ?
          </Typography>

          {holdingToDelete && (
            <Box
              sx={{
                mt: 2,
                p: 1.5,
                bgcolor: "var(--color-bg-secondary)",
                borderRadius: 1,
                border: "1px solid var(--color-border-primary)",
              }}
            >
              <Box display="flex" justifyContent="space-between" mb={0.75}>
                <Typography fontSize="var(--font-size-sm)" color="var(--color-text-secondary)">
                  Quantity
                </Typography>
                <Typography fontSize="var(--font-size-sm)" fontWeight="var(--font-weight-semibold)" color='var(--color-text-secondary)'>
                  {fmtNumber(holdingToDelete.shares, 2)}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography fontSize="var(--font-size-sm)" color="var(--color-text-secondary)">
                  Current Value
                </Typography>
                <Typography fontSize="var(--font-size-sm)" fontWeight="var(--font-weight-semibold)" color="var(--color-success)">
                  {sym}{fmtNumber(holdingToDelete.current_value, 2)}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 2.5, pb: 1.5, borderTop: "1px solid var(--color-border-primary)" }}>
          <Button
            onClick={() => setConfirmDeleteHolding(false)}
            disabled={deletingHolding}
            sx={{
              textTransform: "none",
              fontSize: "var(--font-size-sm)",
              fontWeight: 500,
              color: "var(--color-text-secondary)",
              "&:hover": { bgcolor: "var(--color-surface-hover)" },
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            disabled={deletingHolding}
            onClick={async () => {
              setDeletingHolding(true);
              await onUndoRemove(row.id, holdingToDelete);
              setDeletingHolding(false);
              setConfirmDeleteHolding(false);
              setHoldingToDelete(null);
            }}
            startIcon={
              deletingHolding ? <CircularProgress size={14} color="inherit" /> : null
            }
            sx={{
              textTransform: "none",
              fontSize: "var(--font-size-sm)",
              fontWeight: 500,
              bgcolor: "var(--color-error)",
              "&:hover": { bgcolor: "var(--color-error-hover)" },
              boxShadow: "none",
              py: 0.5,
              px: 1.75,
            }}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const AllPortfolio = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [portfolios, setPortfolios] = useState([]);
  const [expandedMap, setExpandedMap] = useState({});
  const [loadingMap, setLoadingMap] = useState({});
  const [loading, setLoading] = useState(false);

  const [openCreate, setOpenCreate] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const [activePortfolioId, setActivePortfolioId] = useState(null);
  const [snackbar, setSnackbar] = useState(null);
  const [selectedPortfolioIds, setSelectedPortfolioIds] = useState([]);

  const [openRebalance, setOpenRebalance] = useState(false);
  const [openBulkEdit, setOpenBulkEdit] = useState(false);
  const [bulkEditPortfolio, setBulkEditPortfolio] = useState(null);
  const sym = getCurrencySymbol();

  useEffect(() => {
    if (location.state?.openCreatePortfolio) {
      setOpenCreate(true);
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, navigate, location.pathname]);

  useEffect(() => {
    fetchPortfolios();
  }, []);

  const fetchPortfolios = async () => {
    setLoading(true);
    const res = await getAllPortfolios();

    if (!res?.success) {
      setLoading(false);
      return;
    }

    const allPortfolios = res.data || [];

    // ✅ Show the list immediately — loader is gone at this point
    setPortfolios(allPortfolios);
    setLoading(false);

    // 🔄 Fetch individual details in the background, patch each row as it arrives
    allPortfolios.forEach(async (portfolio) => {
      const detailRes = await getPortfolioById(portfolio.id);
      if (!detailRes?.success) return;

      setPortfolios((prev) =>
        prev.map((p) => {
          if (p.id !== portfolio.id) return p;
          return {
            ...p,
            ...detailRes.data,
            // Preserve list-level summary values in case detail API omits them
            total_value: detailRes.data.total_value ?? p.total_value,
            total_cost: detailRes.data.total_cost ?? p.total_cost,
            total_gain_loss: detailRes.data.total_gain_loss ?? p.total_gain_loss,
            total_gain_loss_pct: detailRes.data.total_gain_loss_pct ?? p.total_gain_loss_pct,
            daily_gain_loss: detailRes.data.daily_gain_loss ?? p.daily_gain_loss,
            daily_gain_loss_pct: detailRes.data.daily_gain_loss_pct ?? p.daily_gain_loss_pct,
          };
        })
      );
    });
  };

  const handlePortfolioUpdated = (updatedPortfolio) => {
    if (!updatedPortfolio) {
      fetchPortfolios();
      return;
    }

    setPortfolios((prev) =>
      prev.map((p) => (p.id === updatedPortfolio.id ? updatedPortfolio : p))
    );
  };

  const reloadPortfolioById = async (id) => {
    setLoadingMap((p) => ({ ...p, [id]: true }));
    const res = await getPortfolioById(id);
    if (res?.success) {
      setPortfolios((prev) =>
        prev.map((p) => {
          if (p.id !== id) return p;
          // Merge: keep existing summary fields if detail API doesn't provide them
          return {
            ...p,
            ...res.data,
            total_value: res.data.total_value ?? p.total_value,
            total_cost: res.data.total_cost ?? p.total_cost,
            total_gain_loss: res.data.total_gain_loss ?? p.total_gain_loss,
            total_gain_loss_pct: res.data.total_gain_loss_pct ?? p.total_gain_loss_pct,
            daily_gain_loss: res.data.daily_gain_loss ?? p.daily_gain_loss,
            daily_gain_loss_pct: res.data.daily_gain_loss_pct ?? p.daily_gain_loss_pct,
          };
        })
      );
    }
    setLoadingMap((p) => ({ ...p, [id]: false }));
  };

  const toggleExpand = async (id) => {
    const isCurrentlyExpanded = expandedMap[id];
    setExpandedMap((p) => ({ ...p, [id]: !p[id] }));

    if (!isCurrentlyExpanded) {
      const p = portfolios.find((x) => x.id === id);
      if (!p?.holdings) {
        await reloadPortfolioById(id);
      }
    }
  };

  const handleUndoRemove = async (portfolioId, holding) => {
    await removeHoldingFromPortfolio(portfolioId, holding.ticker);
    await reloadPortfolioById(portfolioId);
    setSnackbar({ message: `${holding.ticker} removed` });
  };

  const handleCollapseAll = () => {
    setExpandedMap({});
  };

  const handleExpandAll = async () => {
    const newExpandedMap = {};
    portfolios.forEach(p => {
      newExpandedMap[p.id] = true;
    });
    setExpandedMap(newExpandedMap);

    const fetchPromises = portfolios
      .filter(p => !p.holdings)
      .map(p => reloadPortfolioById(p.id));

    await Promise.all(fetchPromises);
  };

  const handleRebalance = async (portfolioId, updatedPortfolio) => {
    await fetchPortfolios();

    const portfolio = portfolios.find(p => p.id === portfolioId);
    setSnackbar({
      message: `Portfolio "${portfolio?.name || 'Portfolio'}" rebalanced successfully`
    });
  };

  const togglePortfolioSelection = (portfolioId) => {
    setSelectedPortfolioIds(prev => {
      if (prev.includes(portfolioId)) {
        return prev.filter(id => id !== portfolioId);
      }
      return [...prev, portfolioId];
    });
  };

  const selectAllPortfolios = () => {
    setSelectedPortfolioIds(portfolios.map(p => p.id));
  };

  const clearPortfolioSelection = () => {
    setSelectedPortfolioIds([]);
  };

  const activePortfolioHoldings =
    portfolios.find((p) => p.id === activePortfolioId)?.holdings || [];

  const EmptyPortfolios = ({ onCreate }) => (
    <Box
      sx={{
        bgcolor: "var(--color-surface)",
        borderRadius: 1.5,
        border: "1px solid var(--color-border-primary)",
        py: 5,
        px: 3,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 1.5,
          bgcolor: "var(--color-surface-hover)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 1.5,
        }}
      >
        <FolderOpenOutlinedIcon sx={{ fontSize: 20, color: "var(--color-text-secondary)" }} />
      </Box>

      <Typography fontSize="var(--font-size-sm)" fontWeight="var(--font-weight-semibold)" color="var(--color-text-primary)">
        No portfolios yet
      </Typography>

      <Typography fontSize="var(--font-size-sm)" color="var(--color-text-secondary)" maxWidth={320} mt={0.5} lineHeight={1.6}>
        Create your first portfolio to track holdings, performance, dividends,
        and gains.
      </Typography>

      <Button
        variant="contained"
        startIcon={<AddIcon sx={{ fontSize: 16 }} />}
        sx={{
          mt: 2,
          px: 2,
          py: 0.65,
          fontSize: "var(--font-size-sm)",
          fontWeight: 500,
          textTransform: "none",
          borderRadius: 1,
          bgcolor: "var(--color-primary)",
          "&:hover": {
            opacity: 0.9,
          },
        }}
        onClick={onCreate}
      >
        Create portfolio
      </Button>
    </Box>
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "var(--color-bg-primary)",
        px: { xs: 2, md: 4 },
        py: 3.5,
        pb: { xs: 2, md: "var(--spacing-2xl)" },
      }}
    >
      {/* Header + Total Basis Row */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        flexDirection={{ xs: "column", md: "row" }}
        gap={2}
      >
        <Box
          style={{
            display: "flex",
            flexDirection: "column"
          }}
          sx={{
            mb: "var(--spacing-lg)",
            display: "flex",
            flexDirection: "column",
            gap: "var(--spacing-xs)",
          }}>
          <Typography
            variant='title'
            fontWeight="var(--font-weight-bold)"
            className="text-primary"
            sx={{ color: 'var(--color-text-primary)' }}
            mb={0.25}
            letterSpacing="-0.01em"
          >
            Portfolio Analytics
          </Typography>

          <Typography
            variant='subtitle'
            sx={{ color: 'var(--color-text-secondary)' }}
            fontWeight={400}
          >
            Multi-asset tracking with professional performance indicators.
          </Typography>
        </Box>
        {market !== "ALL" &&
          <Box
            sx={{
              bgcolor: "var(--color-surface)",
              border: "1px solid var(--color-border-primary)",
              borderRadius: 1,
              px: 2.5,
              py: 1.25,
              // minWidth: 200,
              width: { xs: "100%", md: "auto" },
              minWidth: { md: 200 },
              textAlign: "right",
            }}
          >
            <Typography
              fontSize="var(--font-size-sm)"
              fontWeight="var(--font-weight-semibold)"
              color="var(--color-text-secondary)"
              letterSpacing="0.08em"
              textTransform="uppercase"
              mb={0.25}
            >
              Total Invested
            </Typography>

            <Typography fontSize="var(--font-size-lg)" fontWeight="var(--font-weight-bold)" color="var(--color-success)">
              {sym}{fmtNumber(
                portfolios.reduce((sum, p) => {
                  // Use total_cost from the summary API if available
                  if (p.total_cost != null) {
                    return sum + Number(p.total_cost);
                  }
                  // Fallback to computing from holdings
                  const holdings = p.holdings || [];
                  return (
                    sum +
                    holdings.reduce(
                      (hSum, h) =>
                        hSum +
                        Number(h.cost_basis || 0) * Number(h.shares || 0),
                      0
                    )
                  );
                }, 0),
                0
              )}
            </Typography>
          </Box>
        }
      </Box>

      {/* Action Bar */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        flexWrap="wrap"
        gap={1.5}
      >
        <Box display="flex" gap={1} flexWrap="wrap">
          <Button
            startIcon={<UnfoldMoreIcon sx={{ fontSize: 18 }} />}
            onClick={handleExpandAll}
            disabled={loading}
            sx={{
              bgcolor: "var(--color-surface)",
              color: "var(--color-text-primary)",
              fontSize: "var(--font-size-sm)",
              fontWeight: "var(--font-weight-semibold)",
              textTransform: "none",
              px: 1.75,
              py: 0.5,
              borderRadius: 1,
              border: "1px solid var(--color-border-primary)",
              "&:hover": {
                bgcolor: "var(--color-surface-hover)",
              },
            }}
          >
            Expand All
          </Button>

          <Button
            startIcon={<UnfoldLessIcon sx={{ fontSize: 18 }} />}
            onClick={handleCollapseAll}
            disabled={loading}
            sx={{
              bgcolor: "var(--color-surface)",
              color: "var(--color-text-primary)",
              fontSize: "var(--font-size-sm)",
              fontWeight: "var(--font-weight-semibold)",
              textTransform: "none",
              px: 1.75,
              py: 0.5,
              borderRadius: 1,
              border: "1px solid var(--color-border-primary)",
              "&:hover": {
                bgcolor: "var(--color-surface-hover)",
              },
            }}
          >
            Collapse All
          </Button>

          <Button
            startIcon={<BalanceIcon sx={{ fontSize: 18 }} />}
            onClick={() => setOpenRebalance(true)}
            disabled={loading || portfolios.length === 0 || market === "ALL"}
            sx={{
              bgcolor: "var(--color-surface)",
              color: "var(--color-text-primary)",
              fontSize: "var(--font-size-sm)",
              fontWeight: "var(--font-weight-semibold)",
              textTransform: "none",
              px: 1.75,
              py: 0.5,
              borderRadius: 1,
              border: "1px solid var(--color-border-primary)",
              "&:hover": {
                bgcolor: "var(--color-surface-hover)",
              },
            }}
          >
            Equal Weights
          </Button>
        </Box>

        <Box display="flex" gap={1.5} alignItems="center" flexWrap="wrap">
          {market !== "ALL" &&
            <Button
              startIcon={<AddIcon sx={{ fontSize: 18 }} />}
              onClick={() => setOpenCreate(true)}
              sx={{
                bgcolor: "var(--color-primary)",
                color: "#fff",
                fontSize: "var(--font-size-sm)",
                fontWeight: "var(--font-weight-semibold)",
                textTransform: "none",
                px: 2,
                py: 0.75,
                borderRadius: 1,
                "&:hover": {
                  opacity: 0.9,
                },
              }}
            >
              New Portfolio
            </Button>
          }
          {/* <Button
            startIcon={<TrendingUpIcon sx={{ fontSize: 18 }} />}
            onClick={() => navigate("/portfolioHealth")}
            sx={{
              background: "var(--color-primary)",
              color: "#fff",
              fontSize: "var(--font-size-sm)",
              fontWeight: "var(--font-weight-semibold)",
              textTransform: "none",
              px: 2.5,
              py: 1,
              borderRadius: 1.5,
              "&:hover": {
                opacity: 0.9,
                transform: "translateY(-1px)",
              },
              transition: "all 0.2s ease",
            }}
          >
            Check Portfolio Health
          </Button> */}
        </Box>
      </Box>

      {/* Portfolios Table */}
      {market === "ALL" &&
        <Alert
          severity="info"
          sx={{
            mb: 2,
            bgcolor: "var(--color-info-light)",
            color: "var(--color-text-primary)",
            border: "1px solid var(--color-info)",
            borderRadius: 1,
            fontSize: "var(--font-size-sm)",
            fontWeight: "var(--font-weight-medium)",
            "& .MuiAlert-icon": {
              color: "var(--color-info)",
            },
          }}
        >
          Select a specific market to add or edit your portfolio holdings.
        </Alert>
      }

      <Box
        sx={{
          bgcolor: "var(--color-surface)",
          border: "1px solid var(--color-border-primary)",
          borderRadius: 1.5,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            width: "100%",
            overflowX: "auto",
            WebkitOverflowScrolling: "touch",
          }}
        >
          {loading ? (
            <Box
              sx={{
                width: "100%",
                overflowX: "auto",
                WebkitOverflowScrolling: "touch",
              }}
            >
              <PortfolioTableSkeleton />
            </Box>
          ) : portfolios.length === 0 ? (
            <EmptyPortfolios onCreate={() => setOpenCreate(true)} />
          ) : (
            <Table
              size="small"
              sx={{
                minWidth: 900, // forces horizontal scroll on small screens
              }}
            >              <TableHead>
                <TableRow sx={{ bgcolor: "var(--color-surface-hover)" }}>
                  {HEADER_COLUMNS.map((header, idx) => (
                    <TableCell
                      key={idx}
                      sx={{
                        fontSize: "var(--font-size-sm)",
                        fontWeight: "var(--font-weight-bold)",
                        color: "var(--color-text-primary)",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        borderBottom: "1px solid var(--color-border-primary)",
                        py: 1.25,
                      }}
                    >
                      <Tooltip title={HEADER_TOOLTIPS[header] ?? ""} placement="top" arrow>
                        <span style={{ cursor: "default" }}>{header}</span>
                      </Tooltip>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {portfolios.map((p) => (
                  <PortfolioRow
                    key={p.id}
                    row={p}
                    expanded={!!expandedMap[p.id]}
                    loading={!!loadingMap[p.id]}
                    onToggle={toggleExpand}
                    onAdd={(id) => {
                      setActivePortfolioId(id);
                      setOpenAdd(true);
                    }}
                    onBulkEdit={(portfolio) => {
                      setBulkEditPortfolio(portfolio);
                      setOpenBulkEdit(true);
                    }}
                    onUndoRemove={handleUndoRemove}
                    onNameUpdated={handlePortfolioUpdated}
                    isSelected={selectedPortfolioIds.includes(p.id)}
                    onToggleSelection={togglePortfolioSelection}
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </Box>
      </Box>

      {/* Asset Concentration Panel */}
      {market !== "ALL" &&
        <Box
          sx={{
            mt: 2,
            width: "100%",
            bgcolor: "var(--color-surface)",
            border: "1px solid var(--color-border-primary)",
            borderRadius: 1.5,
            overflow: "hidden",
          }}
        >
          <Box
            px={2}
            py={1.25}
            borderBottom="1px solid var(--color-border-primary)"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography
              fontSize="var(--font-size-base)"
              fontWeight="var(--font-weight-bold)"
              color="var(--color-text-primary)"
              textTransform="uppercase"
            >
              Asset Concentration
            </Typography>
            {selectedPortfolioIds.length > 0 && (
              <Chip
                label={`${selectedPortfolioIds.length} selected`}
                size="small"
                sx={{
                  bgcolor: "var(--color-primary)",
                  color: "#fff",
                  fontSize: "var(--font-size-sm)",
                  height: 24,
                  fontWeight: "var(--font-weight-semibold)",
                }}
              />
            )}
          </Box>

          {/* Portfolio Selection Controls */}
          {portfolios.length > 0 && (
            <Box
              px={2}
              py={1.25}
              borderBottom="1px solid var(--color-border-primary)"
              display="flex"
              gap={0.75}
              flexWrap="wrap"
            >
              <Button
                size="small"
                onClick={selectAllPortfolios}
                sx={{
                  fontSize: "var(--font-size-sm)",
                  fontWeight: "var(--font-weight-semibold)",
                  color: "var(--color-primary)",
                  textTransform: "none",
                  minWidth: "auto",
                  p: 0.5,
                  "&:hover": {
                    bgcolor: "transparent",
                    textDecoration: "underline",
                  },
                }}
              >
                Select All
              </Button>
              <Typography fontSize="var(--font-size-sm)" color="var(--color-text-secondary)" sx={{ alignSelf: "center" }}>
                •
              </Typography>
              <Button
                size="small"
                onClick={clearPortfolioSelection}
                disabled={selectedPortfolioIds.length === 0}
                sx={{
                  fontSize: "var(--font-size-sm)",
                  fontWeight: "var(--font-weight-semibold)",
                  color: selectedPortfolioIds.length > 0 ? "var(--color-error)" : "var(--color-text-secondary)",
                  textTransform: "none",
                  minWidth: "auto",
                  p: 0.5,
                  "&:hover": {
                    bgcolor: "transparent",
                    textDecoration: selectedPortfolioIds.length > 0 ? "underline" : "none",
                  },
                  "&.Mui-disabled": {
                    color: "var(--color-text-secondary)",
                  },
                }}
              >
                Clear
              </Button>
            </Box>
          )}

          {loading ? (
            <AssetConcentrationSkeleton />
          ) : (
            <Box p={2}>
              {portfolios.length === 0 ||
                portfolios.every(p => !p.holdings || p.holdings.length === 0) ? (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  minHeight={200}
                  textAlign="center"
                >
                  <Typography fontSize="var(--font-size-sm)" color="var(--color-text-secondary)">
                    Add assets to see<br />concentration breakdown
                  </Typography>
                </Box>
              ) : (
                <>
                  {/* Concentration Chart + Top Holdings in a row layout */}
                  <Box display="flex" gap={3} flexDirection={{ xs: "column", md: "row" }}>
                    {/* Concentration Chart */}
                    <Box
                      sx={{
                        width: { xs: "100%", md: 280 },
                        minWidth: 280,
                        height: 200,
                        bgcolor: "var(--color-bg-secondary)",
                        borderRadius: 1.5,
                        border: "1px solid var(--color-border-primary)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        position: "relative",
                        flexShrink: 0,
                      }}
                    >
                      {(() => {
                        const filteredPortfolios = selectedPortfolioIds.length > 0
                          ? portfolios.filter(p => selectedPortfolioIds.includes(p.id))
                          : portfolios;

                        const allHoldings = filteredPortfolios.flatMap(p => p.holdings || []);
                        const totalValue = allHoldings.reduce(
                          (sum, h) => sum + Number(h.current_value || 0),
                          0
                        );

                        const holdingMap = {};
                        allHoldings.forEach(h => {
                          if (!holdingMap[h.ticker]) {
                            holdingMap[h.ticker] = {
                              ticker: h.ticker,
                              value: 0,
                            };
                          }
                          holdingMap[h.ticker].value += Number(h.current_value || 0);
                        });

                        const chartData = Object.values(holdingMap)
                          .map(h => ({
                            name: h.ticker,
                            value: h.value,
                            percentage: totalValue > 0 ? (h.value / totalValue) * 100 : 0,
                          }))
                          .sort((a, b) => b.value - a.value);

                        const colors = [
                          "#3b82f6",
                          "#10b981",
                          "#f59e0b",
                          "#ef4444",
                          "#8b5cf6",
                          "#ec4899",
                          "#06b6d4",
                          "#84cc16",
                          "#f97316",
                          "#6366f1",
                        ];

                        if (chartData.length === 0) {
                          return (
                            <Typography fontSize="var(--font-size-sm)" color="var(--color-text-secondary)" textAlign="center">
                              {selectedPortfolioIds.length > 0
                                ? "No holdings in selected portfolios"
                                : "No data available"}
                            </Typography>
                          );
                        }

                        return (
                          <>
                            <PieChart width={200} height={200}>
                              <Pie
                                data={chartData}
                                cx={100}
                                cy={100}
                                innerRadius={50}
                                outerRadius={80}
                                paddingAngle={2}
                                dataKey="value"
                              >
                                {chartData.map((entry, index) => (
                                  <Cell
                                    key={`cell-${index}`}
                                    fill={colors[index % colors.length]}
                                  />
                                ))}
                              </Pie>
                              <Tooltip
                                content={({ active, payload }) => {
                                  if (active && payload && payload.length) {
                                    const data = payload[0].payload;
                                    return (
                                      <Box
                                        sx={{
                                          bgcolor: "var(--color-surface)",
                                          border: "1px solid var(--color-border-primary)",
                                          borderRadius: 1,
                                          p: 1,
                                        }}
                                      >
                                        <Typography fontSize="var(--font-size-sm)" fontWeight="var(--font-weight-semibold)" color='var(--color-text-secondary)'>
                                          {data.name}
                                        </Typography>
                                        <Typography fontSize="var(--font-size-sm)" color="var(--color-success)">
                                          {sym}{fmtNumber(data.value, 0)}
                                        </Typography>
                                        <Typography fontSize="var(--font-size-sm)" color='var(--color-text-muted)'>
                                          {fmtNumber(data.percentage, 1)}%
                                        </Typography>
                                      </Box>
                                    );
                                  }
                                  return null;
                                }}
                              />
                            </PieChart>

                            <Box
                              sx={{
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                textAlign: "center",
                                pointerEvents: "none",
                              }}
                            >
                              <Typography fontSize="var(--font-size-sm)" color="var(--color-text-secondary)" fontWeight="var(--font-weight-semibold)">
                                Total
                              </Typography>
                              <Typography fontSize="var(--font-size-base)" fontWeight="var(--font-weight-bold)" color="var(--color-success)">
                                {sym}{fmtNumber(totalValue / 1000, 1)}k
                              </Typography>
                            </Box>
                          </>
                        );
                      })()}
                    </Box>

                    {/* Top Holdings */}
                    <Box flex={1}>
                      <Typography
                        fontSize="var(--font-size-sm)"
                        fontWeight="var(--font-weight-semibold)"
                        color='var(--color-text-secondary)'
                        textTransform="uppercase"
                        letterSpacing="0.05em"
                        mb={1.5}
                      >
                        Top Holdings
                      </Typography>

                      {(() => {
                        const filteredPortfolios = selectedPortfolioIds.length > 0
                          ? portfolios.filter(p => selectedPortfolioIds.includes(p.id))
                          : portfolios;

                        const allHoldings = filteredPortfolios.flatMap(p => p.holdings || []);

                        const totalValue = allHoldings.reduce(
                          (sum, h) => sum + Number(h.current_value || 0),
                          0
                        );

                        const holdingMap = {};
                        allHoldings.forEach(h => {
                          if (!holdingMap[h.ticker]) {
                            holdingMap[h.ticker] = {
                              ticker: h.ticker,
                              company_name: h.company_name,
                              value: 0,
                            };
                          }
                          holdingMap[h.ticker].value += Number(h.current_value || 0);
                        });

                        const topHoldings = Object.values(holdingMap)
                          .sort((a, b) => b.value - a.value)
                          .slice(0, 5);

                        return topHoldings.map((holding, index) => {
                          const percentage =
                            totalValue > 0 ? (holding.value / totalValue) * 100 : 0;

                          return (
                            <Box
                              key={holding.ticker}
                              sx={{
                                py: 1,
                                borderBottom:
                                  index < topHoldings.length - 1
                                    ? "1px solid var(--color-border-primary)"
                                    : "none",
                              }}
                            >
                              <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                mb={0.5}
                              >
                                <Box>
                                  <Typography
                                    fontSize="var(--font-size-base)"
                                    fontWeight="var(--font-weight-semibold)"
                                    color='var(--color-text-secondary)'
                                  >
                                    {holding.ticker}
                                  </Typography>
                                  <Typography fontSize="var(--font-size-sm)" color='var(--color-text-secondary)' noWrap>
                                    {holding.company_name}
                                  </Typography>
                                </Box>

                                <Box textAlign="right">
                                  <Typography
                                    fontSize="var(--font-size-base)"
                                    fontWeight="var(--font-weight-semibold)"
                                    color='var(--color-text-secondary)'
                                  >
                                    {sym}{fmtNumber(holding.value, 0)}
                                  </Typography>
                                  <Typography fontSize="var(--font-size-base)" color='var(--color-text-secondary)'>
                                    {fmtNumber(percentage, 1)}%
                                  </Typography>
                                </Box>
                              </Box>

                              <Box
                                sx={{
                                  width: "100%",
                                  height: 3,
                                  bgcolor: "var(--color-surface-hover)",
                                  borderRadius: 0.5,
                                  overflow: "hidden",
                                }}
                              >
                                <Box
                                  sx={{
                                    width: `${percentage}%`,
                                    height: "100%",
                                    bgcolor: "var(--color-primary)",
                                    transition: "width 0.3s ease",
                                  }}
                                />
                              </Box>
                            </Box>
                          );
                        });
                      })()}
                    </Box>

                    {/* Summary */}
                    <Box
                      sx={{
                        // width: { xs: "100%", md: 200 },
                        // minWidth: 200,
                        width: { xs: "100%", md: "auto" },
                        minWidth: { md: 200 },
                        flexShrink: 0,
                        pt: { xs: 2, md: 0 },
                        borderTop: { xs: "1px solid var(--color-border-primary)", md: "none" },
                        borderLeft: { xs: "none", md: "1px solid var(--color-border-primary)" },
                        pl: { xs: 0, md: 3 },
                      }}
                    >
                      {(() => {
                        const filteredPortfolios = selectedPortfolioIds.length > 0
                          ? portfolios.filter(p => selectedPortfolioIds.includes(p.id))
                          : portfolios;

                        const totalAssets = filteredPortfolios.reduce(
                          (sum, p) => sum + (p.holdings_count || (p.holdings || []).length),
                          0
                        );

                        const totalValue = filteredPortfolios.reduce((sum, p) => {
                          // Prefer summary-level total_value
                          if (p.total_value != null) {
                            return sum + Number(p.total_value);
                          }
                          return (
                            sum +
                            (p.holdings || []).reduce(
                              (hSum, h) => hSum + Number(h.current_value || 0),
                              0
                            )
                          );
                        }, 0);

                        return (
                          <>
                            <Box display="flex" justifyContent="space-between" mb={1}>
                              <Typography fontSize="var(--font-size-sm)" color='var(--color-text-secondary)'>
                                Total Assets
                              </Typography>
                              <Typography fontSize="var(--font-size-sm)" fontWeight="var(--font-weight-semibold)" color='var(--color-text-secondary)'>
                                {totalAssets}
                              </Typography>
                            </Box>

                            <Box display="flex" justifyContent="space-between" mb={1}>
                              <Typography fontSize="var(--font-size-sm)" color='var(--color-text-secondary)'>
                                Total Value
                              </Typography>
                              <Typography fontSize="var(--font-size-sm)" fontWeight="var(--font-weight-semibold)" color="var(--color-success)">
                                {sym}{fmtNumber(totalValue, 0)}
                              </Typography>
                            </Box>

                            <Box display="flex" justifyContent="space-between">
                              <Typography fontSize="var(--font-size-sm)" color='var(--color-text-secondary)'>
                                Portfolios
                              </Typography>
                              <Typography fontSize="var(--font-size-sm)" fontWeight="var(--font-weight-semibold)" color='var(--color-text-secondary)'>
                                {selectedPortfolioIds.length > 0
                                  ? `Selected ${selectedPortfolioIds.length} of ${portfolios.length}`
                                  : portfolios.length}
                              </Typography>
                            </Box>
                          </>
                        );
                      })()}
                    </Box>
                  </Box>
                </>
              )}
            </Box>
          )}
        </Box>
      }

      {/* Dialogs */}
      <CreatePortfolio
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        onCreated={fetchPortfolios}
      />

      <AddHoldingsDialog
        open={openAdd}
        onClose={() => setOpenAdd(false)}
        portfolioId={activePortfolioId}
        existingHoldings={activePortfolioHoldings}
        onSaved={() => reloadPortfolioById(activePortfolioId)}
      />

      <RebalanceDialog
        open={openRebalance}
        onClose={() => setOpenRebalance(false)}
        portfolios={portfolios}
        onRebalance={handleRebalance}
      />

      <BulkEditDialog
        open={openBulkEdit}
        onClose={() => {
          setOpenBulkEdit(false);
          setBulkEditPortfolio(null);
        }}
        portfolio={bulkEditPortfolio}
        onSaved={() => {
          if (bulkEditPortfolio) {
            reloadPortfolioById(bulkEditPortfolio.id);
          }
        }}
      />

      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        message={snackbar?.message}
        onClose={() => setSnackbar(null)}
        ContentProps={{
          sx: {
            fontSize: "var(--font-size-sm)",
            bgcolor: "var(--color-surface)",
            color: "var(--color-text-primary)",
            borderRadius: 1,
            border: "1px solid var(--color-border-primary)",
            minWidth: 0,
          },
        }}
      />
    </Box>
  );
};

export default AllPortfolio;